<template>
  <div class="affiliate-payments">
    <div class="toolbar">
      <div class="wrapper">
        <div class="section">
          <DefaultFilter
            :model-value="filterParams"
            :filters-list="filtersList"
            @submit="handlePaymentsFilter"
          />
        </div>
        <div class="section">
          <div class="pagination">
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count"
              show-size-select
              @page-change="pageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      ref="wrapper"
      class="table-wrapper"
    >
      <UiTable
        v-loading="isDataLoading"
        :fields="fields"
        :data="data"
        :rows-count="limit"
        :sort="{
          prop: sortProp,
          order: sortOrder,
        }"
        lazy-loading
        is-static
        i18n-path="affiliates.payments"
        element-loading-custom-class="data-loader-spinner"
        @sort="handleSort"
      >
        <template v-slot:append>
          <ElTableColumn
            :width="44"
            align="right"
            header-align="right"
          >
            <template v-slot="scope">
              <ActionIcon
                icon="eye"
                :tooltip="$t('affiliates.affiliatePayments.info')"
                :router-to="`/payments/info/${scope.row.id}`"
              />
            </template>
          </ElTableColumn>
        </template>
      </UiTable>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { NAV_SET_SUB_VIEW } from '@/store/action_types/nav';
import { pageSizeMixin, resolvePageSize } from '@/service/pageSize';
import updateUrl from '@/service/updateUrl';


const sizes = {
  1080: 20,
  1440: 25,
};

const viewName = 'affiliate/affiliate_payments/limit'; // для локалсторедж
const pageSize = resolvePageSize(viewName, {
  _default: 15,
  sizes,
});
export default {
  name: 'AffiliatePayments',
  mixins: [pageSizeMixin],
  data() {
    return {
      viewName,
      sortProp: 'incrementing_id',
      sortOrder: 'desc',
      id: '',
      info: {},
      count: 0,
      page: 1,
      moderation_status: ['on_review', 'approved', 'declined'],
      payout_status: ['not_paid', 'for_rework', 'processing', 'paid', 'failed', 'cancelled'],
      limit: pageSize,
      dataQuery: {
        sort_column: 'incrementing_id',
        sort_dir: 'desc',
        moderation_status: [],
        payout_status: [],
        type: '',
      },
      isDataLoading: false,
      fields: [
        {
          name: 'incrementing_id',
          align: 'left',
          sortable: 'custom',
          width: '70',
        },
        {
          name: 'type',
          align: 'left',
          width: '100',
          computedValue: this.getPaymentTypeLabel,
        },
        {
          name: 'created_at',
          align: 'left',
          width: '130',
          format: 'format-date',
          sortable: 'custom',
        },
        {
          name: 'amount',
          align: 'right',
          minWidth: '92',
          class: 'bold',
          format: 'formatMoney',
          computedClass: this.getCurrencyClass,
          sortable: 'custom',
        },
        {
          name: 'moderation_status',
          align: 'left',
          minWidth: '100',
          computedValue: this.formatStatus,
          unsortable: true,
          sortable: false,
        },
        {
          name: 'payout_status',
          align: 'left',
          minWidth: '100',
          computedValue: this.formatStatus,
          unsortable: true,
          sortable: false,
        },
        {
          name: 'payment_period_end',
          align: 'left',
          width: '170',
          computedValue: this.computePaymentPeriod,
          sortable: false,
        },
        {
          name: 'approved_by_admin_name',
          align: 'left',
          minWidth: '140',
          overflow: true,
          sortable: false,
        },
        {
          name: 'site_payment_method_name',
          align: 'center',
          headerAlign: 'center',
          width: '120',
          minWidth: '120',
          class: 'text',
          computedImg: (row) => row.site_payment_method_logo_base64,
          computedImgNull: (_, row) => row.site_payment_method_name || '',
        },
        {
          name: 'payment_wallet',
          align: 'left',
          headerAlign: 'center',
          width: '120',
          minWidth: '120',
          actionLib: 'fa',
          controlType: 'icon',
          computedClass: (_, row) => (row?.payment_wallet ? 'copy-reverse' : 't:!items-center t:cursor-default'),
          action: () => {},
          controlColorIco: () => 'var(--text-primary)',
          actionName: (row) => (row.payment_wallet ? 'clone' : ''),
          computedValueHtml: (row) => row.payment_wallet || '',
          click: this.copyWallet,
        },
        {
          name: 'paid_at',
          format: 'date-time',
          width: '134',
          align: 'left',
          headerAlign: 'left',
          sortable: 'custom',
        },
      ],
      actions: {
        align: 'center',
        operations: {
          accept: {
            class: 'act blue',
            callback: this.infoRedirect,
            callbackOptions: {},
          },
        },
      },

      data: [],
      totals: {},
      defaultSort: {},
      getPayments: Function,

    };
  },
  created() {
    /*
    * Считываем hash
    * */
    const _paramsHash = updateUrl.getParseParams(this.$route.hash);

    if (!this.$_.isEmpty(_paramsHash)) {
      this.dataQuery.sort_column = this.$_.get(_paramsHash, ['dataQuery', 'sort_column']) || 'incrementing_id';
      this.dataQuery.sort_dir = this.$_.get(_paramsHash, ['dataQuery', 'sort_dir']) || 'desc';
      this.dataQuery.moderation_status = this.$_.get(_paramsHash, ['dataQuery', 'moderation_status']) || [];
      this.dataQuery.payout_status = this.$_.get(_paramsHash, ['dataQuery', 'payout_status']) || [];
      this.dataQuery.type = this.$_.get(_paramsHash, ['dataQuery', 'type']) || '';

      this.limit = Number(this.$_.get(_paramsHash, ['limit'])) || pageSize;
      this.page = Number(this.$_.get(_paramsHash, ['page'])) || 1;
    } else {
      this.dataQuery.sort_column = this.$ls.get('affiliate/payments/sort_column') || 'incrementing_id';
      this.dataQuery.sort_dir = this.$ls.get('affiliate/payments/sort_dir') || 'desc';
      this.sortProp = this.$ls.get('affiliate/payments/sort_column') || 'incrementing_id';
      this.sortOrder = this.$ls.get('affiliate/payments/sort_dir') || 'desc';

      this.limit = +this.$ls.get('affiliate/affiliate_payments/limit') || pageSize;
    }

    this.getPayments = this.$_.debounce(this.getPaymentsD, 500);
    this.defaultSort = {
      prop: this.dataQuery.sort_column,
      order: `${this.dataQuery.sort_dir}ending`,
    };
    this.getPayments();
  },
  mounted() {
    this.id = this.$route.params.id;
    if (!this.id) {
      this.$router.push('/affiliates/list');
    }
    this.$api
      .getAffiliateInfo(this.id)
      .then((response) => {
        this.info = this.$_.cloneDeepWith(response.data.payload);
        if (this.info.account_status === 'pending') {
          this.$store.commit(`nav/${NAV_SET_SUB_VIEW}`, 'review');
        } else {
          this.$store.commit(`nav/${NAV_SET_SUB_VIEW}`, 'list');
        }
      });
  },

  methods: {
    async copyWallet(row) {
      if (!row.payment_wallet) return;

      try {
        await this.$clipboard(row.payment_wallet);
        this.$eventBus.$emit('notify-event', {
          type: 'info',
          text: this.$t('reports.other.copied_to_clipboard'),
        });
      } catch (error) {
        this.$eventBus.$emit('notify-event', {
          type: 'error',
          text: this.$t('reports.other.failed_to_copy'),
        });
      }
    },
    handlePaymentsFilter(data) {
      this.dataQuery.type = data?.type || '';
      this.dataQuery.moderation_status = data?.moderation_status || [];
      this.dataQuery.payout_status = data?.payout_status || [];
      this.page = 1;
      this.getPayments();
    },
    infoRedirect(row) {
      this.$router.push(`/payments/info/${row.id}`);
    },
    getPaymentsD() {
      this.isDataLoading = true;
      const q = {
        affiliate_id: this.id,
        limit: this.limit,
        offset: (this.limit * this.page) - this.limit,
        ...this.dataQuery,
      };
      /*
      * Hash function
      * */
      this._completedFilterUrl();

      this.$api
        .getPaymentsHistory(q)
        .then((response) => {
          this.count = response.data.misc.count;
          this.data = this.$_.cloneDeep(response.data.payload.data);
          this.totals = this.$_.clone(response.data.payload.totals);
        })
        .finally(() => {
          this.isDataLoading = false;
        });
    },

    pageChange(page, size) {
      this.page = page;
      this.limit = size;
      this.getPayments();
    },

    formatDate(value) {
      return value ? format(value, 'dd MMM yyyy') : '';
    },
    formatStatus(_, value) {
      return this.$t(`affiliates.affiliatePayments.statusFilter.${value}`);
    },
    getPaymentTypeLabel(row) {
      return row.type ? this.$t(`payments.types.${row.type}`) : '';
    },
    computePaymentPeriod(row) {
      if (!row?.payment_period_start && !row?.payment_period_end) return '';
      return `${this.formatDate(row?.payment_period_start)} - ${this.formatDate(row.payment_period_end)}`;
    },
    getCurrencyClass(value) {
      return value < 0 ? 'negative' : '';
    },
    handleSort({ prop, order }) {
      if (order === '') return;
      if (this.dataQuery.sort_column !== prop) {
        this.sortProp = prop;
      } else {
        this.sortOrder = order;
      }

      this.dataQuery.sort_column = this.sortProp;
      this.dataQuery.sort_dir = this.sortOrder;
      this.$ls.set(
        'affiliate/payments/sort_column',
        this.dataQuery.sort_column,
      );
      this.$ls.set(
        'affiliate/payments/sort_dir',
        this.dataQuery.sort_dir,
      );
      this.getPayments();
    },
    /*
     * Создаем filter URL
     * */
    _completedFilterUrl() {
      const _dataFilters = {
        dataQuery: this.dataQuery,
        limit: this.limit,
        page: this.page,
      };
      updateUrl.updateGetParams(_dataFilters);
    },
  },
  computed: {
    filterParams() {
      const params = {};
      if (this.dataQuery.type) {
        params.type = this.dataQuery.type;
      }
      if (this.dataQuery.moderation_status?.length) {
        params.moderation_status = this.dataQuery.moderation_status;
      }
      if (this.dataQuery.payout_status?.length) {
        params.payout_status = this.dataQuery.payout_status;
      }
      return params;
    },
    filtersList() {
      return [
        {
          id: 'type',
          name: 'paymentType',
          type: 'select',
          params: {
            list: [
              { id: 'internal', name: this.$t('payments.types.internal') },
              { id: 'external', name: this.$t('payments.types.external') },
            ],
          },
        },
        {
          id: 'moderation_status',
          name: 'moderationStatus',
          type: 'select',
          params: {
            multiple: true,
            'collapse-tag-tooltip': true,
            list: this.moderation_status.map((id) => ({
              id,
              name: this.$t(`affiliates.affiliatePayments.statusFilter.${id}`),
            })),
          },
        },
        {
          id: 'payout_status',
          name: 'payoutStatus',
          type: 'select',
          params: {
            multiple: true,
            'collapse-tag-tooltip': true,
            list: this.payout_status.map((id) => ({
              id,
              name: this.$t(`affiliates.affiliatePayments.statusFilter.${id}`),
            })),
          },
        },
      ];
    },
  },
};
</script>
